import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MinusCircle, Plus, Trash2 } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const typeColor = {
  fuel: "bg-amber-100 text-amber-700",
  management: "bg-indigo-100 text-indigo-700",
  penalty: "bg-red-100 text-red-700",
  insurance: "bg-blue-100 text-blue-700",
  vehicle: "bg-slate-100 text-slate-700",
  damage: "bg-orange-100 text-orange-700",
  advance_payment: "bg-fuchsia-100 text-fuchsia-700",
  uniform: "bg-teal-100 text-teal-700",
  other: "bg-slate-100 text-slate-700",
};

const Deductions = () => {
  const { data, isLoading } = useSWR("/deductions", fetcher);
  const { data: types } = useSWR("/deductions/types", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: periods } = useSWR("/pay-periods", fetcher);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    driver_id: "", type: "fuel", amount: "", notes: "", pay_period_id: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_id || !form.type || !form.amount) {
      toast.error("Driver, type and amount are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/deductions", {
        ...form,
        amount: parseFloat(form.amount),
      });
      toast.success("Deduction added");
      setOpen(false);
      setForm({ driver_id: "", type: "fuel", amount: "", notes: "",
                pay_period_id: form.pay_period_id, date: new Date().toISOString().slice(0, 10) });
      mutate("/deductions");
      mutate("/dashboard/kpis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this deduction?")) return;
    try {
      await api.delete(`/deductions/${id}`);
      toast.success("Deleted");
      mutate("/deductions");
      mutate("/dashboard/kpis");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Payroll · this period</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Deductions</h1>
          <p className="text-sm text-slate-500 mt-1">Fuel, penalties, damages — auto-applied to driver pay.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="deduction-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add deduction
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New deduction</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Driver</Label>
                <Select value={form.driver_id} onValueChange={(v) => setForm((f) => ({ ...f, driver_id: v }))}>
                  <SelectTrigger data-testid="ded-driver"><SelectValue placeholder="Choose driver" /></SelectTrigger>
                  <SelectContent>
                    {(drivers || []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="ded-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(types || []).map((t) => (
                        <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (£)</Label>
                  <Input data-testid="ded-amount" type="number" step="0.01" value={form.amount}
                         onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Pay period</Label>
                  <Select value={form.pay_period_id} onValueChange={(v) => setForm((f) => ({ ...f, pay_period_id: v }))}>
                    <SelectTrigger data-testid="ded-period"><SelectValue placeholder="Current period" /></SelectTrigger>
                    <SelectContent>
                      {(periods || []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} · {p.status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Date</Label>
                  <Input type="date" value={form.date}
                         onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
                <Textarea rows={2} value={form.notes}
                          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="ded-save" className="bg-brand-red hover:bg-brand-red-hover" disabled={saving}>
                  {saving ? "Saving…" : "Save deduction"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="deductions-table">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Notes</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data || []).map((d) => {
                const drv = (drivers || []).find((x) => x.id === d.driver_id);
                return (
                  <tr key={d.id} data-testid={`ded-row-${d.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{ddmmyyyy(d.date)}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-brand-text">{drv?.full_name || "—"}</div>
                      <div className="text-[11px] font-mono text-slate-500">{drv?.driver_code || d.driver_id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${typeColor[d.type] || "bg-slate-100"}`}>
                        {d.type.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-heading font-bold">{gbp(d.amount)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[240px] truncate">{d.notes || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => del(d.id)} data-testid={`ded-del-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {(data || []).length === 0 && !isLoading && (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500 text-sm">
                    <MinusCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    No deductions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Deductions;
